import { useEffect, useMemo, useRef } from 'react'
import type { Editor, Extensions } from '@tiptap/core'
import type { Node as ProseMirrorNode } from '@tiptap/pm/model'
import { useEditor } from '@tiptap/react'
import Placeholder from '@tiptap/extension-placeholder'
import { richProseExtensions } from '~/design-system/components/DeckGen/components/editor/proseExtensions'
import {
  markdownInlineToText,
  markdownToTiptapNodes,
  tiptapNodesToMarkdown,
  RICH_MARKDOWN,
  type TiptapDoc,
} from '~/design-system/components/Overview/overview.utils'

export const DOC_TITLE_PLACEHOLDER = 'Title'
export const DOC_BODY_PLACEHOLDER = 'Write your ideas...'

// A document gets the full rich schema (headings, bold/italic, lists, links, quotes, code, task
// lists, images — see proseExtensions) — unlike the canvas editor which strips most of it. No diff
// extensions: a user doc has no AI-diff overlay.
// The placeholder is per-node so a fresh (empty) doc reads as a TITLE line + a DESCRIPTION line;
// showOnlyCurrent:false makes both show at once — and makes them show in the read-only preview
// (which has no cursor/anchor, so the default current-node-only behavior would render nothing).
//
// We DON'T want the body placeholder on every new empty line the user makes by hitting Enter —
// only while the doc is genuinely empty (its seeded title + body). `editor.isEmpty` is true exactly
// in that state and flips false the moment any real text is typed, so adding empty paragraphs with
// Enter (which doesn't add text) keeps it empty — but those extra paragraphs are gated out by the
// `isFirstParagraph` check (first paragraph node only). Result: a blank doc shows "Title" / "Write
// your ides"; once you start writing or press Enter, new empty lines show just the cursor.
const documentEditorExtensions = [
  ...richProseExtensions,
  Placeholder.configure({
    showOnlyCurrent: false,
    // The read-only preview has no editing — without this the plugin renders no placeholders there.
    showOnlyWhenEditable: false,
    placeholder: ({ editor, node }: { editor: Editor; node: ProseMirrorNode }) => {
      if (node.type.name === 'heading') return DOC_TITLE_PLACEHOLDER
      // Body placeholder only on the doc's seed body line — the FIRST paragraph, and only while the
      // whole doc is still empty (`editor.isEmpty`). A blank preview card is also "empty", so the
      // preview keeps showing it. The moment the user types, isEmpty flips false; empty paragraphs
      // added with Enter aren't the first paragraph anyway → no placeholder, just the bare cursor.
      const isSeedBody = editor.isEmpty && node === firstParagraphOf(editor.state.doc)
      return isSeedBody ? DOC_BODY_PLACEHOLDER : ''
    },
  }),
]

/** The doc's first paragraph node (the seed body line), matched by identity so a long title — which
 *  shifts ProseMirror positions — never knocks it out of range. Docs open as [heading, paragraph]. */
const firstParagraphOf = (doc: ProseMirrorNode): ProseMirrorNode | null => {
  let found: ProseMirrorNode | null = null
  doc.forEach((child) => {
    if (!found && child.type.name === 'paragraph') found = child
  })
  return found
}

const markdownToTiptapDoc = (markdown: string): TiptapDoc => {
  const nodes = markdownToTiptapNodes(markdown, RICH_MARKDOWN)
  // Empty doc → seed a title (H1) + a description (paragraph) so both placeholders render.
  if (nodes.length === 0) {
    return { type: 'doc', content: [{ type: 'heading', attrs: { level: 1 } }, { type: 'paragraph' }] }
  }
  return { type: 'doc', content: nodes }
}

/** True when the markdown carries no actual text (only structural markers / whitespace) — used to
 *  store an empty doc as '' rather than the seeded `# `/empty-paragraph scaffold. The class covers
 *  every marker the rich dialect can emit, so an empty checkbox (`- [ ]`) still counts as blank. */
const isBlankMarkdown = (markdown: string): boolean => markdown.replace(/[#*_>\s\-[\]()`~|]/g, '').length === 0

/**
 * Tiptap editor for a user document card. Content is a single markdown string (`DocumentCard.content`)
 * — round-tripped through the shared overview.utils converters. Pass `onContentChange` for an editable
 * instance; omit it for a read-only render (the preview card). Modeled on useCanvasEditor, minus the
 * diff machinery and with the full StarterKit so docs support headings + lists.
 */
export const useDocumentEditor = (
  content: string,
  onContentChange?: (markdown: string) => void,
  // Whether the editor accepts input right now. Defaults to "editable when it has a save handler",
  // but callers can force it read-only (e.g. during an open/close morph) while keeping the save
  // handler wired, so the lightweight read-only editor animates smoothly and edits still save once
  // it settles. onUpdate only fires on real user edits, which can't happen while non-editable.
  editable: boolean = !!onContentChange,
) => {
  const lastSavedContentRef = useRef<string>(content)

  const editorContent = useMemo(() => markdownToTiptapDoc(content), [content])

  const editor = useEditor({
    extensions: documentEditorExtensions as Extensions,
    content: editorContent,
    editable,
    onUpdate: ({ editor: activeEditor }) => {
      const json = activeEditor.getJSON()
      const markdown = tiptapNodesToMarkdown(json.content || [])
      // Store a content-less doc as '' so it re-seeds the title/description scaffold next open
      // (and `deriveDocTitle` falls back to 'Untitled') instead of persisting `# `/blank lines.
      const normalized = isBlankMarkdown(markdown) ? '' : markdown
      if (normalized !== lastSavedContentRef.current) {
        lastSavedContentRef.current = normalized
        onContentChange?.(normalized)
      }
    },
  })

  useEffect(() => {
    editor?.setEditable(editable)
  }, [editor, editable])

  // Re-sync external content changes (e.g. undo/redo) when the user isn't actively typing.
  useEffect(() => {
    if (editor && !editor.isFocused) {
      lastSavedContentRef.current = content
      editor.commands.setContent(editorContent, { emitUpdate: false })
    }
  }, [editor, content, editorContent])

  return editor
}

/** First non-empty line of the markdown, stripped of heading/list markers AND of inline syntax — used
 *  as the card's display title (keeps `DocumentCard.title` meaningful now that the doc is one Tiptap
 *  body). Without the inline strip a pasted article titled `[Foo](https://…)` names the card with the
 *  raw brackets and URL; an image-only first line yields '' and falls through to the next line. */
export const deriveDocTitle = (markdown: string): string => {
  for (const rawLine of markdown.split('\n')) {
    const stripped = rawLine
      .replace(/^\s*#{1,6}\s+/, '')
      .replace(/^\s*(?:[-*+]|\d+[.)])\s+/, '')
      .replace(/^\[[ xX]\]\s*/, '')
      .replace(/^\s*>\s?/, '')
    const line = markdownInlineToText(stripped).trim()
    if (line) return line
  }
  return 'Untitled'
}
